import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { onAuthenticationRequired } from "@/lib/auth-events";
import { useStore } from "@/state/useState";
import {
  acquireTeamsAccessToken,
  acquireTeamsAccessTokenInteractive,
  isInteractionRequired,
} from "@/lib/teams/auth";
import {
  initializeTeams,
  notifyTeamsAppLoaded,
  notifyTeamsAppReady,
} from "@/lib/teams/host";

type GateState = "initializing" | "authenticated" | "needsSignIn" | "failed";

/**
 * Auth gate for the Microsoft Teams static tab.
 *
 * Initializes the Teams SDK, notifies the host the tab loaded, then acquires a
 * backend access token via Teams SSO. Children render only once a token has been
 * obtained; the app's API client (`acquireAccessToken`) then routes through the
 * same Teams path for subsequent calls.
 *
 * Silent first, always: where SSO works the user sees nothing but the spinner.
 * When it does not, the gate offers a sign-in button rather than ending in an
 * error, which is what it used to do (MA-133).
 *
 * That dead end was not hypothetical. On Windows the Teams desktop client brokers
 * tokens through the OS, which cannot answer silently unless the machine itself is
 * signed in with the work account. For a user on a personal or shared Windows
 * device -- work account in Teams only -- the silent path can never succeed, and
 * the tab was simply unusable while the browser client worked fine for them. The
 * button is the whole remedy for that audience: one prompt, then the account is
 * cached (see `cacheLocation` in lib/teams/auth.ts) and later opens are silent
 * again.
 *
 * The gate also comes *back* to that prompt, not just arrives at it: it listens
 * for `onAuthenticationRequired`, which the API layer raises when a token cannot
 * be acquired or the backend refuses one. Holding a token was never the same as
 * having a working session -- the gate does not call the API -- and without the
 * signal a refused token rendered a chat in which every request failed, silently
 * (MA-202).
 *
 * Re-authenticating unmounts and remounts the children, so the on-screen
 * conversation is rebuilt from the session rather than preserved in place.
 * Acceptable for now; keeping it mounted behind an overlay is the refinement.
 */
export default function TeamsAuthGate({
  children,
}: {
  children: React.ReactNode;
}) {
  const [state, setState] = useState<GateState>("initializing");
  const [reason, setReason] = useState<string>("");
  // Whether the Teams handshake completed. Without a host there is no broker to
  // prompt through, so the failure screen must not offer a sign-in that cannot
  // work -- it offers nothing but the advice to reopen the tab.
  const [hasHost, setHasHost] = useState(false);
  const attempted = useRef(false);
  const fetchedConfig = useRef(false);
  const fetchConfig = useStore((state) => state.fetchConfig);

  // Come back to the sign-in prompt when the API reports that credentials no
  // longer work.
  //
  // Two cases, and the gate could see neither on its own. It only ever *acquires*
  // a token; it never calls the API. So a token the backend refuses rendered a
  // fully working-looking chat in which every request failed — which is exactly
  // what the Windows-desktop 401 looked like. And later in a session, once the
  // MSAL cache entry expires, the silent path asks the OS broker again, fails
  // again, and there was no way back to a prompt because the gate had already
  // passed.
  //
  // Guarded on `authenticated`: while the gate is initializing or already showing
  // a prompt, its own flow owns the state, and a late signal from an in-flight
  // request must not overwrite it.
  useEffect(
    () =>
      onAuthenticationRequired(() => {
        setState((current) => {
          if (current !== "authenticated") return current;
          console.warn("[teams] session no longer valid; asking to sign in again");
          setReason("");
          return "needsSignIn";
        });
      }),
    [],
  );

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    (async () => {
      try {
        await initializeTeams();
        notifyTeamsAppLoaded();
        setHasHost(true);
      } catch (e) {
        console.error("[teams] host initialization failed", e);
        setReason(describe(e));
        setState("failed");
        return;
      }

      try {
        await acquireTeamsAccessToken();
        notifyTeamsAppReady();
        setState("authenticated");
      } catch (e) {
        // A recoverable refusal earns a prompt; anything else is a real fault and
        // saying "press this button" would only waste the user's time.
        if (isInteractionRequired(e)) {
          console.warn("[teams] silent sign-in unavailable; offering prompt", e);
          setReason(describe(e));
          setState("needsSignIn");
          return;
        }
        console.error("[teams] token acquisition failed", e);
        setReason(describe(e));
        setState("failed");
      }
    })();
  }, []);

  async function signIn() {
    setState("initializing");
    try {
      await acquireTeamsAccessTokenInteractive();
      notifyTeamsAppReady();
      setState("authenticated");
    } catch (e) {
      console.error("[teams] interactive sign-in failed", e);
      setReason(describe(e));
      setState("failed");
    }
  }

  // Fetch the customer config once the tab is authenticated.
  //
  // This gate never did, and nothing else in the Teams tree does either: the web
  // app gets it from `RouterWrapper` and the portal embed from `EmbedAuthGate`,
  // but `TeamsApp` renders this gate around `ChatInterface` directly. So `config`
  // stayed null here forever.
  //
  // That was invisible until it wasn't. Nothing in the Teams tab read `config`
  // until Sam's opening greeting arrived, which lives in the customer config
  // (`chat.greeting`) so the wording is the client's to change without a frontend
  // release. The greeting simply never rendered in Teams -- no error, no warning,
  // just a missing message that worked everywhere else.
  //
  // The ref resets when the tab stops being authenticated, so re-authenticating
  // fetches again. Getting that wrong is how the embed ended up with a config it
  // could never refill after falling back to the sign-in prompt.
  useEffect(() => {
    if (state !== "authenticated") {
      fetchedConfig.current = false;
      return;
    }
    if (fetchedConfig.current) return;

    fetchedConfig.current = true;
    fetchConfig();
  }, [state, fetchConfig]);

  if (state === "authenticated") return <>{children}</>;

  if (state === "needsSignIn") {
    return (
      <div className="grid h-full w-full place-items-center p-4">
        <div className="flex max-w-sm flex-col items-center gap-3 text-center">
          <p className="text-sm text-muted-foreground">
            Meld je aan om Sam in Teams te gebruiken.
          </p>
          <Button onClick={signIn}>Aanmelden</Button>
        </div>
      </div>
    );
  }

  if (state === "failed") {
    return (
      <div className="grid h-full w-full place-items-center p-4">
        <div className="flex max-w-sm flex-col items-center gap-3 text-center">
          <p className="text-sm text-muted-foreground">
            Aanmelden bij Teams is niet gelukt. Probeer het tabblad opnieuw te
            openen.
          </p>
          {/* Surfaced on purpose. The previous version logged the reason to a
              console the affected users cannot open -- diagnosing the Windows
              case took a VM and the client's own trace files. */}
          {reason ? (
            <p className="text-xs text-muted-foreground/70">{reason}</p>
          ) : null}
          {hasHost ? (
            <Button variant="outline" onClick={signIn}>
              Opnieuw proberen
            </Button>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="grid h-full w-full place-items-center text-sm text-muted-foreground">
      Aanmelden…
    </div>
  );
}

/** A short, non-sensitive description of a failure, for display in the tab. */
function describe(e: unknown): string {
  if (e && typeof e === "object" && "errorCode" in e) {
    const code = String((e as { errorCode?: unknown }).errorCode ?? "");
    if (code) return code;
  }
  return e instanceof Error ? e.message.slice(0, 200) : "";
}
